import newMenuItems from 'src/new-menu-items';

// Platform state mapping
export const PLATFORM_INDICES = {
  FLIPKART: 1,
  MYNTRA: 2,
  BLINKIT: 3,
  INSTAMART: 4,
  ZEPTO: 5,
  BIGBASKET: 6,
  SETTINGS: 7,
  SPONSORED_ADS_DASHBOARD: 8
};

/**
 * Extract platform states from common state
 * @param {Object} commonState - The common state object
 * @returns {Object} Object containing all platform states
 */
export const getPlatformStates = (commonState) => {
  const {
    flipkartProfileStatus: flipkartState = false,
    profileStatus: amazonState = false,
    myntraProfileStatus: myntraState = false,
    blinkitProfileStatus: blinkitState = false,
    instamartProfileStatus: instamartState = false,
    amcStatus: amcState = false,
    zeptoProfileStatus: zeptoState = false,
    bigbasketProfileStatus: bigbasketState = false,
  } = commonState?.userInfo || {};

  return {
    flipkartState,
    amazonState,
    myntraState,
    blinkitState,
    instamartState,
    amcState,
    zeptoState,
    bigbasketState
  };
};

/**
 * Get available platforms for non-super admin users
 * @param {Object} platformStates - Platform states object
 * @param {Object} getProfile - User profile object
 * @returns {Array} Array of available platform names
 */
export const getAvailablePlatforms = (platformStates, getProfile) => {
  const { 
    flipkartState, 
    amazonState, 
    myntraState, 
    blinkitState, 
    instamartState, 
    amcState, 
    zeptoState, 
    bigbasketState 
  } = platformStates;

  const availablePlatforms = [];

  // Add platforms based on their states
  if (flipkartState) availablePlatforms.push('flipkart');
  if (myntraState) availablePlatforms.push('myntra');
  if (blinkitState) availablePlatforms.push('blinkit');
  if (instamartState) availablePlatforms.push('instamart');
  if (zeptoState) availablePlatforms.push('zepto');
  if (bigbasketState) availablePlatforms.push('bigbasket');

  // Special case for Amazon/AMC/DSP
  if (amazonState || amcState || getProfile?.dsp_only) {
    availablePlatforms.push('amazon');
  }

  return availablePlatforms;
};

/**
 * Build menu data for super admin users
 * @param {Array} clientMenus - Client-specific menus
 * @param {Object} platformStates - Platform states object
 * @returns {Array} Menu data array for super admin
 */
export const buildSuperAdminMenuData = (clientMenus, platformStates) => {
  const { 
    myntraState, 
    blinkitState, 
    instamartState, 
    zeptoState, 
    bigbasketState 
  } = platformStates;

  const menuData =  clientMenus ? [...clientMenus] : [];
  const amazonMenu = menuData?.find((menu) => menu?.title?.toLowerCase() === 'amazon');

  // Always include Sponsored Ads Dashboard for super admin inside amazon menu
  if (amazonMenu && amazonMenu.items && Array.isArray(amazonMenu.items)) {
    const sponsoredAdsItem = newMenuItems[PLATFORM_INDICES.SPONSORED_ADS_DASHBOARD];
    if (sponsoredAdsItem) {
      amazonMenu.items.push(sponsoredAdsItem);
    }
  }

  return [
    ...menuData,
    newMenuItems[PLATFORM_INDICES.FLIPKART], // Always include Flipkart for super admin
    newMenuItems[PLATFORM_INDICES.MYNTRA],
    newMenuItems[PLATFORM_INDICES.BLINKIT],
    newMenuItems[PLATFORM_INDICES.INSTAMART],
    newMenuItems[PLATFORM_INDICES.ZEPTO], // Always include Zepto for super admin
    newMenuItems[PLATFORM_INDICES.BIGBASKET], // Include Bigbasket only if state is true
    newMenuItems[PLATFORM_INDICES.SETTINGS], // Always include Settings for super admin
  ].filter(Boolean);
};

/**
 * Build menu data for regular users based on platform states
 * @param {Array} clientMenus - Client-specific menus
 * @param {Object} platformStates - Platform states object
 * @param {Object} getProfile - User profile object
 * @returns {Array} Menu data array for regular users
 */
export const buildRegularUserMenuData = (clientMenus, platformStates, getProfile) => {
  const { 
    flipkartState, 
    amazonState, 
    myntraState, 
    blinkitState, 
    instamartState, 
    amcState,
    zeptoState, 
    bigbasketState 
  } = platformStates;

  let menuData = [];
  const authToken = JSON.parse(localStorage.getItem('User'));
  const onlyMcp = authToken?.metaDetails?.offering === 'mcp';

  // Start with client menus if Amazon/AMC/DSP is available
  if (amazonState || amcState || getProfile?.dsp_only || onlyMcp) {
    menuData = [...clientMenus];
  }

  // Add Flipkart if available
  if (flipkartState) {
    menuData.push(newMenuItems[PLATFORM_INDICES.FLIPKART]);
  }

  // Add other platforms based on their states
  if (myntraState) {
    menuData.push(newMenuItems[PLATFORM_INDICES.MYNTRA]);
  }

  if (blinkitState) {
    menuData.push(newMenuItems[PLATFORM_INDICES.BLINKIT]);
  }

  if (instamartState) {
    menuData.push(newMenuItems[PLATFORM_INDICES.INSTAMART]);
  }

  if (zeptoState) {
    menuData.push(newMenuItems[PLATFORM_INDICES.ZEPTO]);
  }

  if (bigbasketState) {
    menuData.push(newMenuItems[PLATFORM_INDICES.BIGBASKET]);
  }

  return menuData;
};

/**
 * Main menu building function that determines which menu builder to use
 * @param {Object} platformStates - Platform states object
 * @param {boolean} isSuperAdmin - Whether user is super admin
 * @param {Array} clientMenus - Client-specific menus
 * @param {Object} getProfile - User profile object
 * @returns {Array} Final menu data array
 */
export const buildMenuData = (platformStates, isSuperAdmin, clientMenus, getProfile) => {
  if (isSuperAdmin) {
    return buildSuperAdminMenuData(clientMenus, platformStates);
  }
  
  return buildRegularUserMenuData(clientMenus, platformStates, getProfile);
};
